import * as React from "react"

export function useAudioElement(id: string, options?: AudioElement.Options): AudioElement.Return {
  const currentTimeRef = React.useRef(0)
  const subscribersRef = React.useRef(new Set<() => void>())

  const ref = React.useRef<HTMLAudioElement>(null)

  const [state, setState] = React.useState(() => ({
    isPlaying: false,
    isBuffering: false,
    isReady: false,
    duration: 0
  }))

  // Utility to avoid redundant setState calls
  const setIfChanged = (updates: Partial<typeof state>) => {
    setState((prev) => {
      const changed = Object.entries(updates).some(([key, value]) => prev[key as keyof typeof prev] !== value)
      return changed ? { ...prev, ...updates } : prev
    })
  }

  const subscribe = (callback: () => void) => {
    subscribersRef.current.add(callback)
    return () => subscribersRef.current.delete(callback)
  }

  const getServerSnapshot = () => 0

  const startTracking = () => {
    const audio = ref.current
    if (!audio) return
    let animationFrameId: number

    const tick = () => {
      currentTimeRef.current = audio.currentTime
      subscribersRef.current.forEach((cb) => cb())
      animationFrameId = requestAnimationFrame(tick)
    }

    animationFrameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(animationFrameId)
  }

  const getCurrentTime = () =>
    React.useSyncExternalStore(subscribe, () => currentTimeRef.current, getServerSnapshot)

  const updateSource = (src: string) => {
    const audio = ref.current
    if (!audio) return

    try {
      const normalizedSrc = new URL(src, window.location.href).href
      if (audio.src === normalizedSrc) return
    } catch {
      if (audio.src === src) return
    }

    audio.src = src
    audio.load()
    audio.currentTime = 0
  }

  React.useEffect(() => {
    const audio = ref.current
    if (!audio) return

    const controller = new AbortController()
    const signal = controller.signal

    const cleanupRaf = startTracking()

    const onPlay = () => setIfChanged({ isPlaying: true })
    const onPause = () => setIfChanged({ isPlaying: false })

    const onLoadedMetadata = () => {
      setIfChanged({ duration: audio.duration, isReady: true })
    }

    const onEnded = () => {
      setIfChanged({ isPlaying: false })
      options?.onEnded?.(audio)
    }

    const onCanPlay = () => setIfChanged({ isBuffering: false })
    const onWaiting = () => setIfChanged({ isBuffering: true })

    const onEmptied = () => {
      setIfChanged({
        isReady: false,
        isPlaying: false,
        duration: 0
      })
    }

    audio.addEventListener("play", onPlay, { signal })
    audio.addEventListener("pause", onPause, { signal })
    audio.addEventListener("loadedmetadata", onLoadedMetadata, { signal })
    audio.addEventListener("ended", onEnded, { signal })
    audio.addEventListener("canplay", onCanPlay, { signal })
    audio.addEventListener("waiting", onWaiting, { signal })
    audio.addEventListener("emptied", onEmptied, { signal })

    return () => {
      cleanupRaf?.()
      controller.abort()
    }
  }, [options?.onEnded])

  const play = () => {
    const audio = ref.current
    if (!audio) return

    if (audio.readyState >= 3) {
      return audio.play()
    }

    if (audio.readyState <= 2) {
      setIfChanged({ isBuffering: true })
    }

    audio
      .play()
      .catch(() => {
        setIfChanged({ isPlaying: false })
      })
      .finally(() => {
        setIfChanged({ isBuffering: false })
      })
  }

  const end = () => {
    pause()
    const duration = ref.current?.duration || state.duration
    if (duration > 0) {
      seekTo(duration)
    }
  }

  const pause = () => ref.current?.pause()
  const reset = () => seekTo(0)

  const togglePlayPause = () => {
    if (!ref.current) return
    ref.current.paused ? play() : pause()
  }

  const seekTo = (t: number) => {
    if (ref.current) {
      ref.current.currentTime = t
      currentTimeRef.current = t
      subscribersRef.current.forEach((cb) => cb())
    }
  }

  const controls = {
    isPlaying: state.isPlaying,
    duration: state.duration,
    isReady: state.isReady,
    isBuffering: state.isBuffering,
    play,
    end,
    reset,
    pause,
    togglePlayPause,
    seekTo,
    getCurrentTime,
    updateSource
  }

  return [ref, controls]
}

namespace AudioElement {
  type Controls = {
    isPlaying: boolean
    duration: number
    isReady: boolean
    isBuffering: boolean
    play: () => void
    end: () => void
    reset: () => void
    pause: () => void
    togglePlayPause: () => void
    seekTo: (seconds: number) => void
    getCurrentTime: () => number
    updateSource: (src: string) => void
  }
  export type Options = {
    onEnded?: (audio: HTMLAudioElement) => void
  }
  export type Return = [React.RefObject<HTMLAudioElement | null>, Controls]
}
